import Cookies from 'universal-cookie';
import ObexRequestHandler from '../handlers/ObexRequestsHandler';
import SessionService from '../services/SessionsService';
import ObexRequestHandlerPre from '../handlers/ObexRequestsHandlerPre';

// Only value needed from mesh's brunixMasterConfig, inlined rather than pulling in that project.
const BRUNIX_MASTER_IP = 'pre.brunix.mooo.com';

const _cookies = new Cookies();

export interface PrimaryAvsBrunixNode {
  /** Public/routable IP (or an 'unknown (...)' string) — drives X-Target-Ip and resolveRagServer(). */
  ip: string;
  /** LAN-side IP (node_config.local_ip, threaded through PBRXData -> node.data.local_ip), '' if not applicable/resolved. */
  localIp: string;
}

/**
 * Resolves whichever Brunix node is wired to the primary AVS's
 * `brunix_engine` handle — independently of Mesh's own React/zustand store —
 * and returns both its public ip and its LAN-side local_ip in one shot.
 *
 * This used to read Mesh's zustand store directly (`useMeshStore.getState()`),
 * but that store only gets populated once the Mesh Manager screen has been
 * mounted (its `getFlowConfig()` call), so every call site outside Mesh saw
 * an empty store and always logged "unknown". Instead, this fetches the same
 * `/infrastructure_designer_config` endpoint Mesh itself loads from, each
 * time it's called, so it works no matter which screen is open.
 *
 * Note: this deliberately does NOT replicate Mesh's in-memory BRUNIX_MASTER
 * auto-injection (store.js's getFlowConfig) — it only looks at what's
 * actually persisted. If the primary AVS has no persisted brunix_engine
 * edge yet, `ip` falls back to BRUNIX_MASTER_IP, the same default Mesh would
 * show for an unwired AVS.
 *
 * `/infrastructure_designer_config` requires an authenticated session, and
 * ObexRequestHandler redirects to /cloud_login on any non-whitelisted path
 * called without a token — so this must never fire before login (e.g. at
 * module-load time, from a top-level constant). Bail out with no network
 * call at all if there's no session cookie yet.
 *
 * getPrimaryAvsBrunixIp()/getPrimaryAvsBrunixLocalIp() below are thin
 * wrappers kept for callers that only need one field — but each call still
 * does its own fetch, so a caller that needs both (e.g. brunixStream.ts)
 * should call this directly instead, to avoid resolving the flow twice.
 */
export const getPrimaryAvsBrunixNode = async (): Promise<PrimaryAvsBrunixNode> => {
  const user = _cookies.get('101Obex');
  if (!user?.token) return { ip: 'unknown (not logged in)', localIp: '' };
  try {
    const res = await ObexRequestHandlerPre.get('/infrastructure_designer_config', { org_id: 2897/*SessionService.ORGANIZATION*/ });
    const flow_config = res?.data ?? res;
    if (!flow_config) return { ip: `unknown (no infrastructure config saved yet, default: ${BRUNIX_MASTER_IP})`, localIp: '' };

    // Same double-encoding defensiveness as store.js's getFlowConfig.
    let flow = flow_config;
    if (typeof flow === 'string') flow = JSON.parse(flow);
    if (typeof flow === 'string') flow = JSON.parse(flow);
    if (!flow || !Array.isArray(flow.nodes)) return { ip: 'unknown (unexpected infrastructure config shape)', localIp: '' };

    const avsNode = flow.nodes.find((n: any) => n.type === 'AVSNode' && n.data?.primary_avs === true)
      ?? flow.nodes.find((n: any) => n.type === 'AVSNode');
    if (!avsNode) return { ip: 'unknown (no primary AVS node found)', localIp: '' };

    const edges: any[] = Array.isArray(flow.edges) ? flow.edges : [];
    const edge = edges.find((e: any) => e.source === avsNode.id && e.sourceHandle === 'source-brunix_engine');
    // No brunix node wired to the primary AVS's brunix_engine handle at
    // all — return the master default directly (not an 'unknown (...)'
    // string) so callers like brunixStream.ts's targetIpHeader() forward
    // it as X-Target-Ip instead of omitting the header. Every other branch
    // here means some brunix node IS wired, just broken/incomplete, and
    // keeps returning 'unknown (...)' so the header is omitted, same as
    // before. The master node never has a LAN ip either way.
    if (!edge) return { ip: BRUNIX_MASTER_IP, localIp: '' };

    const brunixNode = flow.nodes.find((n: any) => n.id === edge.target);
    if (!brunixNode) return { ip: `unknown (brunix_engine edge has no matching node, default: ${BRUNIX_MASTER_IP})`, localIp: '' };

    // The Master node's ip/port are a frontend-only constant, not backend
    // truth: whatever got persisted last time the flow was saved can be
    // stale (e.g. the constant changed since), and store.js's own loader
    // always overrides it with the CURRENT constant on every load (see
    // getFlowConfig's masterData injection). Only a real physical node
    // (BrunixEngineNodeF) has its ip/local_ip legitimately sourced from the
    // backend — the master has no physical machine behind it, so no LAN ip.
    if (brunixNode.type === 'BrunixEngineNode') return { ip: BRUNIX_MASTER_IP, localIp: '' };

    return {
      ip: brunixNode.data?.ip ?? `unknown (brunix node has no ip, default: ${BRUNIX_MASTER_IP})`,
      localIp: brunixNode.data?.local_ip ?? '',
    };
  } catch (err) {
    return { ip: `unknown (error: ${err?.message ?? err})`, localIp: '' };
  }
};

export const getPrimaryAvsBrunixIp = async (): Promise<string> => (await getPrimaryAvsBrunixNode()).ip;

export const getPrimaryAvsBrunixLocalIp = async (): Promise<string> => (await getPrimaryAvsBrunixNode()).localIp;
